import { useState } from 'react';
import { Collapse, Dropdown, Image, Nav } from 'react-bootstrap';
import { FaChevronDown, FaChevronRight, FaUserCircle } from 'react-icons/fa';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

const moduleNavMap = {
    crm: [
        { label: 'Accounts', slug: 'customers' },
        { label: 'New Application', slug: 'new-application' },

        {
            label: 'Customer Type',
            slug: 'customer-type',
            children: [
                { label: 'Residential', slug: 'residential' },
                { label: 'Commercial', slug: 'commercial' },
                { label: 'Government', slug: 'government' },
                { label: 'Industrial', slug: 'industrial' }
            ]
        },

        {
            label: 'Customer Rate Class',
            slug: 'customer-rate-class',
            children: [
                { label: 'Lifeline', slug: 'lifeline' },
                { label: 'Regular', slug: 'regular' },
                { label: 'Senior Citizen', slug: 'senior' }
            ]
        },

        {
            label: 'Verification',
            slug: 'verification',
            children: [
                { label: 'Pending', slug: 'pending' },
                { label: 'In Progress', slug: 'in-progress' },
                { label: 'Completed', slug: 'done' }
            ]
        },

        { label: 'Blacklist / Watchlist', slug: 'blacklist' },
        {
            label: 'Documents',
            slug: 'documents',
            children: [
                { label: 'Valid IDs', slug: 'ids' },
                { label: 'Proof of Billing', slug: 'proof-of-billing' },
                { label: 'Contracts', slug: 'contracts' }
            ]
        }
    ],

    billing: [

        {
            label: 'Meter Reading',
            slug: 'meter-reading',
            children: [
                { label: 'Pending', slug: 'pending' },
                { label: 'Completed', slug: 'Completed' }
            ]
        },
        { label: 'Bill Computation', slug: 'bill-computation' },
        {
            label: 'Bill List',
            slug: 'bill-list',
            children: [
                { label: 'Pending Bills', slug: 'pending' },
                { label: 'Delivered Bills', slug: 'delivered' },
                { label: 'Paid Bills', slug: 'paid' },
                { label: 'Past Due Bills', slug: 'past-due' }
            ]
        },
        {
            label: 'e-Bills',
            slug: 'e-bills',
            children: [
                { label: 'Send e-Bills', slug: 'send' },
                { label: 'View e-Bills', slug: 'view' }
            ]
        },
        {
            label: 'Printable Bills',
            slug: 'printable-bills',
            children: [
                { label: 'For Printing', slug: 'for-printing' },
                { label: 'Printed History', slug: 'printed-history' }
            ]
        },
        {
            label: 'Reports',
            slug: 'billing-reports',
            children: [
                { label: 'Daily Billing Report', slug: 'daily' },
                { label: 'Monthly Summary', slug: 'monthly' },
                { label: 'Revenue Analysis', slug: 'revenue' }
            ]
        }
    ],

    collection: [
        {
            label: 'Payment Processing',
            slug: 'payment-processing',
            children: [
                { label: 'New Payment', slug: 'new-payment' },
                { label: 'Online Payments', slug: 'online' },
                { label: 'Walk-in Payments', slug: 'walk-in' },
            ]
        },
        {
            label: 'Collection List',
            slug: 'collection-list',
            children: [
                { label: 'Unpaid Accounts', slug: 'unpaid' },
                { label: 'Partial Payments', slug: 'partial' },
                { label: 'Fully Paid', slug: 'paid' },
                { label: 'Overdue Accounts', slug: 'overdue' }
            ]
        },
        {
            label: 'Disconnection Notices',
            slug: 'disconnection',
            children: [
                { label: 'For Disconnection', slug: 'for-disconnection' },
                { label: 'Disconnected Accounts', slug: 'disconnected' },
                { label: 'Reconnected Accounts', slug: 'reconnected' }
            ]
        },
        {
            label: 'Payment Verification',
            slug: 'payment-verification',
            children: [
                { label: 'Pending Verification', slug: 'pending' },
                { label: 'Verified Payments', slug: 'verified' }
            ]
        },
        {
            label: 'Collection Reports',
            slug: 'collection-reports',
            children: [
                { label: 'Daily Collection', slug: 'daily' },
                { label: 'Monthly Summary', slug: 'monthly' },
                { label: 'Aging Report', slug: 'aging' },
                { label: 'Disconnection Report', slug: 'disconnection-report' }
            ]
        }
    ],

    field: [
        {
            label: 'Inspection',
            slug: 'inspection',
            children: [
                { label: 'Pending', slug: 'pending' },
                { label: 'Scheduled', slug: 'scheduled' },
                { label: 'Completed', slug: 'done' }
            ]
        },
        {
            label: 'Installation',
            slug: 'installation',
            children: [
                { label: 'Pending', slug: 'pending' },
                { label: 'Scheduled', slug: 'scheduled' },
                { label: 'Completed', slug: 'done' }
            ]
        },
        {
            label: 'Meter Maintenance',
            slug: 'maintenance',
            children: [
                { label: 'Reported Issues', slug: 'issues' },
                { label: 'In Progress', slug: 'in-progress' },
                { label: 'Resolved', slug: 'resolved' }
            ]
        },
        {
            label: 'Disconnection',
            slug: 'disconnection',
            children: [
                { label: 'For Disconnection', slug: 'for-disconnection' },
                { label: 'Disconnected', slug: 'disconnected' },
                { label: 'Reconnection', slug: 'reconnection' }
            ]
        },
        {
            label: 'Technician Assignment',
            slug: 'technician-assignment',
            children: [
                { label: 'Assigned Tasks', slug: 'assigned' },
                { label: 'Unassigned Tasks', slug: 'unassigned' },
                { label: 'Technician List', slug: 'technicians' }
            ]
        },
        {
            label: 'Field Reports',
            slug: 'field-reports',
            children: [
                { label: 'Daily Logs', slug: 'daily' },
                { label: 'Activity Summary', slug: 'summary' },
                { label: 'Incident Reports', slug: 'incidents' }
            ]
        }
    ],

    workorders: [
        {
            label: 'Create Work Order',
            slug: 'create'
        },
        {
            label: 'All Work Orders',
            slug: 'all'
        },
        {
            label: 'Work Order Queue',
            slug: 'queue',
            children: [
                { label: 'New Requests', slug: 'new' },
                { label: 'Assigned', slug: 'assigned' },
                { label: 'In Progress', slug: 'in-progress' },
                { label: 'Completed', slug: 'completed' },
                { label: 'Cancelled', slug: 'cancelled' }
            ]
        },
        {
            label: 'Work Order Types',
            slug: 'types',
            children: [
                { label: 'Maintenance', slug: 'maintenance' },
                { label: 'New Connection', slug: 'new-connection' },
                { label: 'Repair', slug: 'repair' },
                { label: 'Upgrade', slug: 'upgrade' },
                { label: 'Transfer Service', slug: 'transfer' },
                { label: 'Others', slug: 'others' }
            ]
        },
        {
            label: 'Technician Assignment',
            slug: 'assignments'
        },
        {
            label: 'Work Order Reports',
            slug: 'reports',
            children: [
                { label: 'Summary Report', slug: 'summary' },
                { label: 'Monthly Report', slug: 'monthly' },
                { label: 'Technician Report', slug: 'technician' }
            ]
        }
    ],

    reporting: [
        {
            label: 'Customer Reports',
            slug: 'customer',
            children: [
                { label: 'New Customers', slug: 'new-customers' },
                { label: 'Disconnected Customers', slug: 'disconnected' },
                { label: 'Customer Type Summary', slug: 'type-summary' },
                { label: 'Location-Based Report', slug: 'location' }
            ]
        },
        {
            label: 'Billing Reports',
            slug: 'billing',
            children: [
                { label: 'Billing Summary', slug: 'summary' },
                { label: 'Pending Bills', slug: 'pending' },
                { label: 'Paid Bills', slug: 'paid' },
                { label: 'Delivered E-Bills', slug: 'delivered-ebills' },
                { label: 'Past Due', slug: 'past-due' }
            ]
        },
        {
            label: 'Collection Reports',
            slug: 'collection',
            children: [
                { label: 'Daily Collection', slug: 'daily' },
                { label: 'Monthly Collection', slug: 'monthly' },
                { label: 'By Collector', slug: 'by-collector' },
                { label: 'Uncollected Accounts', slug: 'uncollected' }
            ]
        },
        {
            label: 'Field Operations',
            slug: 'field',
            children: [
                { label: 'Inspection Summary', slug: 'inspection' },
                { label: 'Installation Summary', slug: 'installation' },
                { label: 'Work Order Status', slug: 'work-order' }
            ]
        },
        {
            label: 'Financial Reports',
            slug: 'finance',
            children: [
                { label: 'Revenue Report', slug: 'revenue' },
                { label: 'Expenses Report', slug: 'expenses' },
                { label: 'Profit & Loss', slug: 'pl' }
            ]
        },
        {
            label: 'System Logs',
            slug: 'logs',
            children: [
                { label: 'Login History', slug: 'login-history' },
                { label: 'User Activity', slug: 'user-activity' }
            ]
        }
    ],
    admin: [
        {
            label: 'User Management',
            slug: 'users',
            children: [
                { label: 'User List', slug: 'list' },
                { label: 'Roles & Permissions', slug: 'roles' },
                { label: 'Activity Logs', slug: 'logs' }
            ]
        },
        {
            label: 'Customer Setup',
            slug: 'customer-setup',
            children: [
                { label: 'Customer Types', slug: 'types' },
                { label: 'Rate Classes', slug: 'rate-classes' },
                { label: 'Billing Schedules', slug: 'billing-schedules' }
            ]
        },
        {
            label: 'Field Setup',
            slug: 'field-setup',
            children: [
                { label: 'Inspection Teams', slug: 'inspection-teams' },
                { label: 'Assigned Personnel', slug: 'assigned-personnel' },
                { label: 'Routes & Areas', slug: 'routes-areas' }
            ]
        },
        {
            label: 'Billing Setup',
            slug: 'billing-setup',
            children: [
                { label: 'Rates & Charges', slug: 'rates' },
                { label: 'Penalty Rules', slug: 'penalties' },
                { label: 'Due Date Settings', slug: 'due-dates' }
            ]
        },
        {
            label: 'System Settings',
            slug: 'system-settings',
            children: [
                { label: 'General Settings', slug: 'general' },
                { label: 'Company Profile', slug: 'company' },
                { label: 'Backup & Restore', slug: 'backup' }
            ]
        }
    ],
    security: [
        {
            label: 'Access Control',
            slug: 'access-control',
            children: [
                { label: 'Roles & Permissions', slug: 'roles' },
                { label: 'User Restrictions', slug: 'user-restrictions' }
            ]
        },
        {
            label: 'Login & Session',
            slug: 'login-session',
            children: [
                { label: 'Login Attempts', slug: 'attempts' },
                { label: 'Session Logs', slug: 'session-logs' },
                { label: 'Active Sessions', slug: 'active-sessions' }
            ]
        },
        {
            label: 'Audit Trail',
            slug: 'audit-trail',
            children: [
                { label: 'System Logs', slug: 'system-logs' },
                { label: 'Data Changes', slug: 'data-changes' },
                { label: 'User Activities', slug: 'user-activities' }
            ]
        },
        {
            label: 'Password Policy',
            slug: 'password-policy',
            children: [
                { label: 'Requirements', slug: 'requirements' },
                { label: 'Reset Settings', slug: 'reset-settings' },
                { label: 'Expiration Rules', slug: 'expiration' }
            ]
        },
        {
            label: 'System Security',
            slug: 'system-security',
            children: [
                { label: 'IP Whitelisting', slug: 'ip-whitelist' },
                { label: 'API Tokens', slug: 'api-tokens' },
                { label: 'API Key Access Logs', slug: 'api-logs' }
            ]
        }
    ],
};

const Layout = ({ user, setUser }) => {
    const navigate = useNavigate();
    const { module } = useParams();
    const location = useLocation();
    const sidebarItems = moduleNavMap[module] || [];
    const pathParts = location.pathname.split('/').filter(Boolean);

    // track expanded parent menus
    const [openMenus, setOpenMenus] = useState({});

    const toggleMenu = (slug) => {
        setOpenMenus((prev) => ({ ...prev, [slug]: !prev[slug] }));
    };

    const getBreadcrumbLabel = (segment, index) => {
        if (index === 0) return segment.toUpperCase();
        const topLevel = sidebarItems.find(item => item.slug === pathParts[1]);
        if (topLevel?.slug === segment) return topLevel.label;
        const child = topLevel?.children?.find(child => child.slug === segment);
        return child ? child.label : segment;
    };

    const getBreadcrumbPath = (index) => '/' + pathParts.slice(0, index + 1).join('/');

    const handleLogout = () => {
        localStorage.removeItem('ums_user');
        setUser(null);
        navigate('/login');
    };

    return (
        <div className="d-flex">
            {/* Sidebar */}
            <div className="bg-light vh-100 p-3 position-fixed top-0 start-0 border-end overflow-auto" style={{ width: 220, zIndex: 1020 }}>
                <h5 className="text-primary mb-4">UMS: {module?.toUpperCase() || 'Unknown'}</h5>
                {sidebarItems.length === 0 ? (
                    <p className="text-muted">No menu for this module</p>
                ) : (
                    <Nav className="flex-column">
                        {sidebarItems.map((item, i) => (
                            <div key={i}>
                                {item.children ? (
                                    <>
                                        <div
                                            className="d-flex align-items-center justify-content-between fw-bold nav-link"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => toggleMenu(item.slug)}
                                        >
                                            <span>{item.label}</span>
                                            {openMenus[item.slug] ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                                        </div>
                                        <Collapse in={openMenus[item.slug]}>
                                            <div>
                                                <Nav className="flex-column ms-3">
                                                    {item.children.map((child, j) => (
                                                        <Nav.Link
                                                            key={j}
                                                            as={Link}
                                                            to={`/${module}/${item.slug}/${child.slug}`}
                                                            className="small"
                                                        >
                                                            {child.label}
                                                        </Nav.Link>
                                                    ))}
                                                </Nav>
                                            </div>
                                        </Collapse>
                                    </>
                                ) : (
                                    <Nav.Link as={Link} to={`/${module}/${item.slug}`} className="fw-bold">
                                        {item.label}
                                    </Nav.Link>
                                )}
                            </div>
                        ))}
                    </Nav>
                )}
            </div>

            {/* Main Content */}
            <div className="flex-grow-1" style={{ marginLeft: 220 }}>
                {/* Topbar */}
                <div className="d-flex justify-content-end align-items-center px-4 py-2 bg-white shadow-sm">
                    <Dropdown align="end">
                        <Dropdown.Toggle variant="light" className="d-flex align-items-center gap-2 border-0 bg-transparent">
                            {user.avatar ? (
                                <Image src={user.avatar} roundedCircle width={35} height={35} />
                            ) : (
                                <FaUserCircle size={32} />
                            )}
                            <span className="fw-bold">{user.firstName} {user.lastName} {user.username}</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item as={Link} to="/profile">View Profile</Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>

                {/* Breadcrumb */}
                <div className="px-4 pt-3 pb-2">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                                <Link to="/modules">Home</Link>
                            </li>
                            {pathParts.map((segment, index) => (
                                <li
                                    key={index}
                                    className={`breadcrumb-item ${index === pathParts.length - 1 ? 'active' : ''}`}
                                    aria-current={index === pathParts.length - 1 ? 'page' : undefined}
                                >
                                    {index === pathParts.length - 1 ? (
                                        getBreadcrumbLabel(segment, index)
                                    ) : (
                                        <Link to={getBreadcrumbPath(index)}>{getBreadcrumbLabel(segment, index)}</Link>
                                    )}
                                </li>
                            ))}
                        </ol>
                    </nav>
                </div>

                {/* Page Content */}
                <div className="p-4">
                    <Outlet />
                </div>
            </div>
        </div>
    );
};

export default Layout;